import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useRoleBase } from "../../hooks/useRoleBase";
import { Card, PageHeader, EmptyState, Spinner, ICONS } from "../../components/dashboard/DashboardShell";
import { Button } from "../../components/ui/button";
import { timeAgo } from "../../lib/format";

const TYPE_META = {
  order: { icon: "clipboard", color: "text-blue-600 bg-blue-50" }, payment: { icon: "wallet", color: "text-emerald-600 bg-emerald-50" },
  report: { icon: "file", color: "text-violet-600 bg-violet-50" }, review: { icon: "star", color: "text-amber-600 bg-amber-50" },
  chat: { icon: "chat", color: "text-cyan-600 bg-cyan-50" }, verification: { icon: "usercheck", color: "text-indigo-600 bg-indigo-50" },
  info: { icon: "bell", color: "text-slate-600 bg-slate-100" },
};

export default function NotificationsPage() {
  const qc = useQueryClient();
  const base = useRoleBase();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["notifications"], queryFn: () => api.get("/notifications").then((r) => r.data) });
  const readAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const open = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.notification_id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    if (n.ref) navigate(n.type === "chat" ? `${base}/chat/${n.ref}` : `${base}/pesanan/${n.ref}`);
  };
  return (
    <div>
      <PageHeader title="Notifikasi" subtitle={data?.unread ? `${data.unread} notifikasi belum dibaca` : "Semua notifikasi sudah dibaca"}
        action={data?.unread > 0 && <Button variant="outline" onClick={readAll} data-testid="read-all-btn" className="text-xs font-bold rounded-lg h-9"><CheckCheck size={14} className="mr-1.5" /> Tandai semua dibaca</Button>} />
      <Card>
        {isLoading ? <Spinner /> : !data?.items?.length ? <EmptyState icon="bell" title="Belum ada notifikasi" /> : (
          <div className="divide-y divide-slate-50" data-testid="notifications-list">
            {data.items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.info;
              const Icon = ICONS[meta.icon] || Bell;
              return (
                <button key={n.notification_id} onClick={() => open(n)} data-testid={`notif-item-${n.notification_id}`}
                  className={`w-full flex items-start gap-3 px-2 py-3.5 text-left hover:bg-slate-50 rounded-lg transition-colors ${n.read ? "" : "bg-blue-50/40"}`}>
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}><Icon size={17} /></span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] ${n.read ? "font-semibold text-slate-700" : "font-extrabold text-slate-900"}`}>{n.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{n.body}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blue-600 mt-2" />}
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
